/**
 * Deep links embedded in exported PDFs.
 *
 * Joe's suggestion in session 1: a PDF sitting in a client's records is a
 * marketing surface — every link in it should route back into CloudSafe, so the
 * document quietly advertises the platform while also being genuinely more useful
 * (click the job reference, land on the job).
 *
 * Links are absolute so they still work from a downloaded file on someone else's
 * machine. Falls back to the public site when no origin is configured.
 */
const PUBLIC_FALLBACK = 'https://cloud-safe.uk';

/** Absolute origin for links leaving the app. */
export function appOrigin() {
  const env = process.env.REACT_APP_PUBLIC_BASE_URL;
  if (env) return String(env).replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location?.origin && !/localhost|127\.0\.0\.1/.test(window.location.origin)) {
    return window.location.origin.replace(/\/$/, '');
  }
  // On localhost a link to localhost is useless in a client's inbox — point at the
  // real site instead so a shared PDF is never broken.
  return PUBLIC_FALLBACK;
}

const BASE = '/contractor/workspace';
export const linkToJob = (id) => (id ? `${appOrigin()}${BASE}/jobs/${encodeURIComponent(id)}` : null);
export const linkToProperty = (id) => (id ? `${appOrigin()}${BASE}/properties/${encodeURIComponent(id)}` : null);
export const linkToForms = () => `${appOrigin()}${BASE}/forms`;
export const linkToHome = () => appOrigin();

/**
 * Draw a clickable line of text in a PDF (jsPDF).
 * Kept here so every export links the same way and in the same brand colour.
 */
export function pdfLink(doc, text, x, y, url, { size = 9, colour = [26, 128, 229] } = {}) {
  if (!url) { doc.text(String(text), x, y); return; }
  doc.setFontSize(size);
  doc.setTextColor(colour[0], colour[1], colour[2]);
  doc.textWithLink(String(text), x, y, { url });
}

/**
 * "View this online in CloudSafe" strip, stamped on a page footer.
 * Returns the height consumed so callers can lay out around it.
 */
export function pdfBackLink(doc, url, x, y, label = 'View this live in CloudSafe') {
  if (!url) return 0;
  doc.setFontSize(8);
  doc.setTextColor(26, 128, 229);
  doc.textWithLink(`${label} →`, x, y, { url });
  doc.setTextColor(160, 160, 160);
  return 10;
}
